<template>
  <base-view-layout class="contact-show">
    <template v-if="contact" #header>
      <div class="contact-heading">
        <div class="contact-info">
          <h1 class="uppercase">
            {{ contact.name }}
          </h1>
          <p class="type">
            <i class="fa-fw text-blue" :class="IconMap[contact.type]" />
            {{ contact.type }}
            <template v-if="contact.subtype">
              <template v-if="contact.type === ContactTypes.VENDOR">
                - {{ VendorSubtypeLabels[contact.subtype] }}
              </template>
              <template v-if="contact.type === ContactTypes.INTERNAL">
                - {{ InternalSubtypeLabels[contact.subtype] }}
              </template>
            </template>
          </p>
          <p class="mt-2" v-if="contact.title">
            {{ contact.title }}
          </p>
          <p class="mt-2" v-if="contact.company_name">
            {{ contact.company_name }}
          </p>
          <p class="mt-2" v-if="contact.email">
            <a class="text-red hover-bold" :href="`mailto:${contact.email}`">
              {{ contact.email }}
            </a>
          </p>
          <p class="mt-2" v-if="websiteUrl">
            <a class="text-red hover-bold" :href="websiteUrl">
              {{ contact.website_url }}
            </a>
          </p>
          <phone-number-with-type
            class="mt-2"
            :phone-number="contact.primary_phone"
          />
          <phone-number-with-type
            :phone-number="number"
            :key="`project_number_${i}`"
            v-for="(number, i) in contact.additional_phones"
          />
        </div>
        <div class="buttons-container" v-if="$mq !== 'sm'">
          <bdg-button
            class="small ghost mr-2 uppercase"
            @click.native="editContact"
          >
            Edit
          </bdg-button>
          <bdg-button
            class="small ghost uppercase"
            @click.native="deleteContact"
          >
            Delete
          </bdg-button>
        </div>

        <div class="primary-place-container">
          <bdg-aside :horizontal="false" class="mb-2" title="Primary Address" />
          <address-cell :place="contact.primary_place" />
        </div>

        <div class="additional-places-container">
          <template
            v-if="contact.additional_places.length && contact.additional_places"
          >
            <bdg-aside
              :horizontal="false"
              class="mb-2"
              title="Additional Addresses"
            />
            <div class="additional-places">
              <address-cell
                :place="place"
                :key="`place_${place.id}`"
                v-for="place in contact.additional_places"
              />
            </div>
          </template>
        </div>
      </div>
      <template v-if="contactNoteText">
        <bdg-aside :horizontal="false" title="Notes" />
        <div class="notes-container mt-2" v-html="contactNoteText"></div>
      </template>
      <div class="flex">
        <div class="associated-projects mt-10 flex-1">
          <template v-if="projects && projects.length">
            <bdg-aside
              :horizontal="false"
              class="mb-2"
              title="Associated Projects"
            />
            <div class="projects-container">
              <div
                class="project"
                :key="`project_${project.id}`"
                v-for="project in projects"
              >
                <router-link
                  class="text-red hover-bold"
                  :to="{ name: 'projects-show', params: { id: project.id } }"
                >
                  {{ project.name }}
                </router-link>
              </div>
            </div>
          </template>
        </div>
        <div class="associated-contracts mt-10 flex-1">
          <template v-if="contracts && contracts.length">
            <bdg-aside
              :horizontal="false"
              class="mb-2"
              title="Associated Contracts"
            />
            <div class="contracts-container">
              <div
                class="contract"
                :key="`contract_${contract.id}`"
                v-for="contract in contracts"
              >
                <router-link
                  class="text-red hover-bold"
                  :to="{ name: 'contracts-show', params: { id: contract.id } }"
                >
                  {{ contract.name }}
                </router-link>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-view-layout>
</template>

<script>
import BaseViewLayout from "../layouts/BaseViewLayout";
import {
  ContactTypes,
  InternalSubtypeLabels,
  VendorSubtypeLabels
} from "../../constants/contact-enums";
import { ContactIcons } from "../../js-data/icon-maps";
import PhoneNumberWithType from "../../elements/PhoneNumberWithType";
import BdgButton from "../../elements/BdgButton";
import BdgAside from "../../elements/BdgAside";
import AddressCell from "../../elements/table-view/special-cells/AddressCell";
import ContactForm from "../../forms/ContactForm";
import GenericDeleteModal from "../../components/modals/GenericDeleteModal";
import { ApiResponseStatus } from "../../constants/enums";

export default {
  name: "ContactShow",
  components: {
    AddressCell,
    BdgAside,
    BdgButton,
    PhoneNumberWithType,
    BaseViewLayout
  },
  data() {
    return {
      id: this.$route.params.id,
      IconMap: ContactIcons,
      ContactTypes,
      VendorSubtypeLabels,
      InternalSubtypeLabels
    };
  },
  computed: {
    contact() {
      return this.$store.state.Contacts.contact;
    },
    websiteUrl() {
      if (this.contact.website_url) {
        return this.contact.website_url.indexOf("http") === 0
          ? this.contact.website_url
          : `//${this.contact.website_url}`;
      }
      return null;
    },
    contactNoteText() {
      return this.contact.note && this.contact.note.text
        ? this.contact.note.text.replace(/(?:\r\n|\r|\n)/g, "<br>")
        : "";
    },
    projects() {
      if (this.contact && this.contact.projects) {
        const projects = this.contact.projects.slice();
        return projects.sort((a, b) => a.name.localeCompare(b.name));
      }
      return null;
    },
    contracts() {
      if (this.contact && this.contact.contracts) {
        const contracts = this.contact.contracts.slice();
        return contracts.sort((a, b) => a.name.localeCompare(b.name));
      }
      return null;
    }
  },
  created() {
    this.$store.dispatch("getContact", this.id);
  },
  methods: {
    editContact() {
      this.$store.commit("setCreateEditComponentParams", {
        createEditComponent: ContactForm,
        createEditComponentData: this.contact,
        createEditComponentIsShown: true
      });
    },
    deleteContact() {
      this.$store.commit("setConfirmationModalParams", {
        type: "delete",
        headerText: "Delete Confirmation",
        bodyComponent: GenericDeleteModal,
        bodyComponentData: {
          entityName: "contact",
          deleteFunction: () =>
            this.$store.dispatch("deleteContact", this.contact).then(res => {
              if (res === ApiResponseStatus.SUCCESS) {
                this.$router.push({ name: "contacts" });
              }
            })
        },
        isShown: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.contact-show {
  .contact-heading {
    display: grid;
    grid-row-gap: 1.25rem;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto;
    grid-template-areas:
      "contact-info projects buttons"
      "primary-place additional-places additional-places";

    @screen md {
      grid-column-gap: 1.75rem;
    }

    @screen xl {
      grid-column-gap: 3.5rem;

      @apply mb-6;
    }

    .contact-info {
      grid-area: contact-info;

      p {
        @apply leading-tight;

        @screen xl {
          @apply text-lg;
        }

        &.type {
          @apply text-sm uppercase;

          @screen xl {
            @apply text-base;
          }
        }
      }
    }

    .associated-projects {
      grid-area: projects;

      .projects-container {
        @screen lg {
          @apply flex flex-wrap;
        }

        .project {
          @screen lg {
            width: calc(20% - 0.6rem);
          }

          &:not(:nth-child(5n)) {
            @screen lg {
              @apply mr-3;
            }
          }

          &:nth-child(n + 6) {
            @screen lg {
              @apply mt-2;
            }
          }
        }
      }
    }

    .buttons-container {
      grid-area: buttons;
    }

    .primary-place-container {
      grid-area: primary-place;

      .project-contact-info {
        @screen xl {
          @apply text-lg;
        }
      }
    }

    .additional-places-container {
      grid-area: additional-places;

      .additional-places {
        @apply flex flex-wrap;

        .address-cell {
          width: 100%;

          @screen md {
            width: calc(50% - 0.25rem);
          }

          @screen lg {
            width: calc(33.333333% - 0.5rem);
          }

          @screen xl {
            width: calc(25% - 2.25rem);
            @apply text-lg;
          }

          &:not(:nth-child(2n)) {
            @screen md {
              @apply mr-2;
            }

            &:nth-child(3n) {
              @screen lg {
                @apply mr-0;
              }
            }
          }

          &:not(:nth-child(3n)) {
            @screen lg {
              @apply mr-3;
            }

            &:nth-child(4n) {
              @screen xl {
                @apply mr-0;
              }
            }
          }

          &:nth-child(3n):not(:nth-child(4n)),
          &:not(:nth-child(4n)) {
            @screen xl {
              @apply mr-12;
            }
          }

          &:nth-child(n + 2) {
            @apply mt-2;

            @screen md {
              @apply mt-0;
            }
          }

          &:nth-child(n + 3) {
            @screen md {
              @apply mt-2;
            }

            @screen lg {
              @apply mt-0;
            }
          }

          &:nth-child(n + 4) {
            @screen lg {
              @apply mt-2;
            }

            @screen xl {
              @apply mt-0;
            }
          }

          &:nth-child(n + 5) {
            @screen xl {
              @apply mt-2;
            }
          }
        }
      }
    }
  }
}
</style>
